import woveLogo from '@src/assets/wove-logo-rgb.svg'
import { useState, useCallback } from 'react'
import {
  BarChart3,
  FileText,
  Home,
  Mail,
  Menu,
  Settings,
  Upload,
  Users,
  X,
  DownloadCloud,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Sheet,
  SheetContent,
  SheetTrigger,
  Button,
  cn,
} from '@wove/web-ui'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@src/hooks/useAuth'
import { useLogoutMutation } from '@wove/api'
import useUserStore from '@src/state/user'

interface NavItem {
  title: string
  href: string
  icon: React.ReactNode
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/',
    icon: <Home className="h-5 w-5" />,
  },
  {
    title: 'Upload Files',
    href: '/upload',
    icon: <Upload className="h-5 w-5" />,
  },
  {
    title: 'Files',
    href: '/files',
    icon: <FileText className="h-5 w-5" />,
  },
  {
    title: 'Email Copilot',
    href: '/email-copilot',
    icon: <Mail className="h-5 w-5" />,
  },
  {
    title: 'Exports',
    href: '/exports',
    icon: <DownloadCloud className="h-5 w-5" />,
  },
  {
    title: 'Analytics',
    href: '/analytics',
    icon: <BarChart3 className="h-5 w-5" />,
  },
  {
    title: 'Team',
    href: '/team',
    icon: <Users className="h-5 w-5" />,
  },
  {
    title: 'Settings',
    href: '/settings',
    icon: <Settings className="h-5 w-5" />,
  },
]

interface PageHeaderProps {
  className?: string
}

const PageHeader = ({ className = '' }: PageHeaderProps) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const { pathname } = useLocation()
  const auth = useAuth()
  const [, logout] = useLogoutMutation()
  const user = useUserStore((state) => state.user)

  const onLogout = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      await logout({})
      auth.logout()
    },
    [auth, logout],
  )
  return (
    // <header
    //   className={`
    //     sticky top-0 flex justify-center items-center px-6 min-h-[77px]
    //     border-b border-[rgba(70,70,70,0.08)] bg-white relative z-50
    //     ${className}
    //   `}>
    //     <img src={woveLogo} alt="wove logo" />
    //   </header>

    <header
      className={cn(
        'sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6',
        className,
      )}
    >
      <Sheet open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" className="md:hidden">
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle navigation menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 sm:max-w-xs">
          <div className="flex h-full flex-col">
            <div className="flex items-center border-b px-2 py-4">
              <Link
                to="/"
                className="flex items-center gap-2 font-semibold"
                onClick={() => setIsMobileNavOpen(false)}
              >
                <img src={woveLogo} alt="Wove Logo" className="h-8" />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto"
                onClick={() => setIsMobileNavOpen(false)}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close navigation menu</span>
              </Button>
            </div>
            <nav className="grid gap-2 p-4">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-all hover:text-foreground',
                    pathname === item.href && 'bg-muted font-medium text-foreground',
                  )}
                  onClick={() => setIsMobileNavOpen(false)}
                >
                  {item.icon}
                  {item.title}
                </Link>
              ))}
            </nav>
          </div>
        </SheetContent>
      </Sheet>
      <Link to="/" className="flex items-center gap-2">
        <img src={woveLogo} alt="Wove Logo" className="h-8" />
      </Link>
      <nav className="hidden md:flex md:gap-2 lg:gap-4 ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage src="/placeholder-user.jpg" alt="Jay Edlin" />
                <AvatarFallback>
                  {user ? user.firstName[0] + (user.lastName?.[0] || '') : '??'}
                </AvatarFallback>
              </Avatar>
              <span className="sr-only">Toggle user menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              {user ? `${user.firstName} ${user.lastName}` : '??'}
            </DropdownMenuLabel>
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Admin
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/logout" onClick={onLogout}>
                Logout
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </header>
  )
}

export default PageHeader
